import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { formatExpiry } from '@core/utils/format'
import { useEffect } from 'react'
import { getShareExtension, buildFullShareUrl, buildRawUrl } from '@/lib/utils/share-url'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  useColorMode,
} from '@chakra-ui/react'
import { Check, Copy, Link, Key, Home, FileText, FileCode } from 'lucide-react'
import { useCopyToClipboard } from '@/lib/hooks/useCopyToClipboard'

interface CreatedState {
  shareId: string
  managementToken: string
  passcode: string | null
  expiresAt: number
  isPublic?: boolean
  filename?: string
}

export function ShareCreatedPage() {
  const { shareId } = useParams<{ shareId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { colorMode } = useColorMode()
  const state = location.state as CreatedState | null

  const { copy, hasCopied } = useCopyToClipboard()

  useEffect(() => {
    if (!state) {
      navigate('/')
    }
  }, [state, navigate])

  if (!state) {
    return null
  }

  const extension = getShareExtension(state.filename)
  const shareUrl = buildFullShareUrl(shareId!, extension, state.passcode, 'full')
  const manageUrl = `${window.location.origin}/edit/${state.managementToken}`
  const rawUrl = buildRawUrl(shareId!, extension, state.passcode, state.isPublic)

  const copyToClipboard = (text: string, type: 'share' | 'manage' | 'raw') => {
    copy(text, type, 'Copied to clipboard')
  }

  return (
    <VStack spacing={6} maxW="2xl" mx="auto">
      <Box textAlign="center">
        <Flex
          w={16}
          h={16}
          bg="green.100"
          borderRadius="full"
          align="center"
          justify="center"
          mx="auto"
          mb={4}
        >
          <Icon as={Check} boxSize={8} color="green.600" />
        </Flex>
        <Heading size="lg" fontFamily="heading">Share Created!</Heading>
        <Text color="stone.500" mt={2}>
          Your secure Markdown share is ready. Save these links!
        </Text>
      </Box>

      <Card w="full">
        <CardHeader>
          <HStack>
            <Icon as={Link} boxSize={5} />
            <Heading size="md">Share Link</Heading>
          </HStack>
          <Text fontSize="sm" color="stone.500" mt={1}>
            Send this link to your recipient
            {state.isPublic
              ? ' - this is a public share, no passcode needed'
              : state.passcode && ' - passcode is embedded for one-click access'}
          </Text>
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={3} align="stretch">
            <InputGroup>
              <Input
                value={shareUrl}
                readOnly
                fontFamily="mono"
                fontSize="sm"
                bg={colorMode === 'dark' ? 'stone.700' : 'stone.50'}
                pr={12}
              />
              <InputRightElement>
                <IconButton
                  aria-label="Copy share link"
                  icon={hasCopied('share') ? <Check size={16} color="green" /> : <Copy size={16} />}
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(shareUrl, 'share')}
                />
              </InputRightElement>
            </InputGroup>
            {!state.passcode && !state.isPublic && (
              <Text fontSize="sm" color="stone.500">
                Share the passcode separately for added security.
              </Text>
            )}
            <HStack spacing={4} flexWrap="wrap">
              {state.filename && (
                <HStack fontSize="sm" color="stone.500">
                  <Icon as={FileText} boxSize={4} />
                  <Text>{state.filename}</Text>
                </HStack>
              )}
              <Text fontSize="sm" color="stone.500">
                Expires: {formatExpiry(state.expiresAt)}
              </Text>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      <Card w="full">
        <CardHeader>
          <HStack>
            <Icon as={FileCode} boxSize={5} />
            <Heading size="md">Raw Link</Heading>
          </HStack>
          <Text fontSize="sm" color="stone.500" mt={1}>
            {state.isPublic
              ? 'Direct access to raw markdown without headers'
              : state.passcode
              ? 'Raw markdown with passcode in URL'
              : 'Replace YOUR_PASSCODE with the actual passcode'}
          </Text>
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={3} align="stretch">
            <InputGroup>
              <Input
                value={rawUrl}
                readOnly
                fontFamily="mono"
                fontSize="sm"
                bg={colorMode === 'dark' ? 'stone.700' : 'stone.50'}
                pr={12}
              />
              <InputRightElement>
                <IconButton
                  aria-label="Copy raw link"
                  icon={hasCopied('raw') ? <Check size={16} color="green" /> : <Copy size={16} />}
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(rawUrl, 'raw')}
                />
              </InputRightElement>
            </InputGroup>
          </VStack>
        </CardBody>
      </Card>

      <Card w="full" borderColor={colorMode === 'dark' ? 'orange.700' : 'orange.200'} bg={colorMode === 'dark' ? 'orange.900' : 'orange.50'}>
        <CardHeader>
          <HStack color={colorMode === 'dark' ? 'orange.200' : 'orange.800'}>
            <Icon as={Key} boxSize={5} />
            <Heading size="md">Management Link</Heading>
          </HStack>
          <Text fontSize="sm" color={colorMode === 'dark' ? 'orange.300' : 'orange.700'} mt={1}>
            Save this link to revoke or check status of your share
          </Text>
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={3} align="stretch">
            <InputGroup>
              <Input
                value={manageUrl}
                readOnly
                fontFamily="mono"
                fontSize="sm"
                bg={colorMode === 'dark' ? 'stone.800' : 'white'}
                pr={12}
              />
              <InputRightElement>
                <IconButton
                  aria-label="Copy management link"
                  icon={hasCopied('manage') ? <Check size={16} color="green" /> : <Copy size={16} />}
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(manageUrl, 'manage')}
                />
              </InputRightElement>
            </InputGroup>
          </VStack>
        </CardBody>
      </Card>

      <Button variant="outline" w="full" leftIcon={<Home size={16} />} onClick={() => navigate('/')}>
        Create Another Share
      </Button>
    </VStack>
  )
}
